import React, { useEffect, useRef, useState } from 'react';

import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { cn } from '@/helpers/shadCnUtils';

interface ExpandableButtonProps {
  icon: any;
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  isActive?: boolean; // Add this to handle initial expand
  isLoading?: boolean;
  hidden?: boolean;
  isDisabled?: boolean;
}

const ExpandableButton: React.FC<ExpandableButtonProps> = ({
  icon,
  children,
  className,
  onClick,
  isActive = false,
  isLoading = false,
  isDisabled = false,
  hidden = false,
}) => {
  const [hover, setHover] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (buttonRef.current && textRef.current) {
      if (hover) {
        buttonRef.current.style.width = `${textRef.current.scrollWidth + 50}px`;
      } else {
        buttonRef.current.style.width = '40px';
      }
    }
  }, [hover]);

  // Apply the hover effect based on isActive at mount
  useEffect(() => {
    setHover(isActive);
    if (isActive) {
      const timeout = setTimeout(() => {
        setHover(false);
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [isActive]);

  return (
    <button
      ref={buttonRef}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={cn(
        'text-buttonPrimary flex h-8 w-10 items-center justify-center overflow-hidden rounded border py-1 text-xs transition-all duration-300 ease-in-out disabled:cursor-default disabled:text-gray-500 disabled:opacity-50 disabled:hover:bg-transparent',
        className,
        !isActive && 'hover:bg-gray-100',
      )}
      disabled={isLoading || isDisabled}
      style={{
        display: !isLoading && hidden ? 'none' : 'flex',
      }}
    >
      {isLoading ? (
        <FontAwesomeIcon icon={faCircleNotch} className="fa-spin mx-1 h-3 w-3" />
      ) : (
        <FontAwesomeIcon icon={icon} className="mx-1 h-4 w-4" />
      )}
      <span
        ref={textRef}
        className={`overflow-hidden whitespace-nowrap transition-all duration-300 ease-linear ${
          hover ? 'ml-1 w-auto opacity-100' : 'w-0 opacity-0'
        }`}
        style={{
          transition: 'width 0.3s ease, opacity 0.3s ease 0.1s',
        }}
      >
        {children}
      </span>
    </button>
  );
};

export default ExpandableButton;
